import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";

import { useSelectedWorkType } from "../hooks/useSelectedWorkType";
import { WorkTypeOption } from "../OnboardingFlowContainer/InterestedWorkTypeStep/InterestedWorkTypeStepContent";
import { getHasChildcareRoles } from "../util/childcareWorkerStorage";

const UNFAMILIAR_SHIFTS_EDUCATION_SEEN_KEY = "unfamiliar-shifts-education-seen";
const UNFAMILIAR_JOBS_EDUCATION_SEEN_KEY = "unfamiliar-jobs-education-seen";

function buildStorageKey(baseKey: string, workerId: string): string {
  return `${baseKey}::${workerId}`;
}

export function hasSeenUnfamiliarEducation(
  marketplace: "shifts" | "jobs",
  workerId: string
): boolean {
  const key =
    marketplace === "shifts"
      ? UNFAMILIAR_SHIFTS_EDUCATION_SEEN_KEY
      : UNFAMILIAR_JOBS_EDUCATION_SEEN_KEY;
  return isDefined(localStorage.getItem(buildStorageKey(key, workerId)));
}

export function markUnfamiliarEducationAsSeen(
  marketplace: "shifts" | "jobs",
  workerId: string
): void {
  const key =
    marketplace === "shifts"
      ? UNFAMILIAR_SHIFTS_EDUCATION_SEEN_KEY
      : UNFAMILIAR_JOBS_EDUCATION_SEEN_KEY;
  localStorage.setItem(buildStorageKey(key, workerId), "true");
}

type UnfamiliarMarketplace = "shifts" | "jobs" | undefined;

function getUnfamiliarMarketplace(
  selectedWorkType: WorkTypeOption | undefined,
  hasChildcareRoles: boolean
): UnfamiliarMarketplace {
  switch (selectedWorkType) {
    case WorkTypeOption.PERMANENT_PLACEMENTS: {
      return "shifts";
    }
    case WorkTypeOption.PERMANENT_AND_FLEX: {
      return hasChildcareRoles ? "shifts" : "jobs";
    }
    case WorkTypeOption.FLEX: {
      return "jobs";
    }
    default: {
      return undefined;
    }
  }
}

interface UseShouldShowUnfamiliarMarketplaceEducationResult {
  shouldShow: boolean;
  unfamiliarMarketplace: UnfamiliarMarketplace;
  markAsSeen: () => void;
}

export function useShouldShowUnfamiliarMarketplaceEducation(
  currentTab: "shifts" | "jobs"
): UseShouldShowUnfamiliarMarketplaceEducationResult {
  const { data: worker } = useGetWorker();
  const { selectedWorkType } = useSelectedWorkType();
  const hasChildcareRoles = getHasChildcareRoles(worker?.userId ?? "");

  const isEducationFlowEnabled = useCbhFlag(CbhFeatureFlag.ONBOARDING_EDUCATION_FLOWS, {
    defaultValue: false,
  });

  const unfamiliarMarketplace = getUnfamiliarMarketplace(selectedWorkType, hasChildcareRoles);

  const workerId = worker?.userId;
  const isOnUnfamiliarTab = unfamiliarMarketplace === currentTab;
  const hasSeen =
    isOnUnfamiliarTab && isDefined(workerId) && hasSeenUnfamiliarEducation(currentTab, workerId);

  const shouldShow = isDefined(workerId) && isEducationFlowEnabled && isOnUnfamiliarTab && !hasSeen;

  return {
    shouldShow,
    unfamiliarMarketplace,
    markAsSeen: () => {
      if (unfamiliarMarketplace && isDefined(workerId)) {
        markUnfamiliarEducationAsSeen(unfamiliarMarketplace, workerId);
      }
    },
  };
}
